import { jwtDecode } from 'jwt-decode';
import dayjs from 'dayjs';
import { getLocalStorageInstance } from '~/helpers/storage.ts';
import {
  TAccessTokenPayload,
  TCounterpartyAutocompleteOption,
  TCounterpartySearchCachePayload,
  TCounterpartySelectorMode,
  TIdentitySource,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByClient/types.ts';
import {
  counterpartyCodeValidLengths,
  COUNTERPARTY_SEARCH_CACHE_PREFIX,
  identitySourceKeys,
  UNKNOWN_USER_CACHE_KEY,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByClient/constants.ts';

const normalizeIdentityValue = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const getIdentityFromSource = (source: TIdentitySource): string => {
  if (typeof source === 'string' || typeof source === 'number') {
    return normalizeIdentityValue(source);
  }

  if (!source || typeof source !== 'object') {
    return '';
  }

  const sourceObject = source as Record<string, unknown>;
  const identity = identitySourceKeys
    .map((key) => normalizeIdentityValue(sourceObject[key]))
    .find(Boolean);

  return identity ?? '';
};

const getIdentityFromToken = (accessToken?: string | null): string => {
  if (!accessToken) {
    return '';
  }

  try {
    const payload = jwtDecode<TAccessTokenPayload>(accessToken);

    return (
      normalizeIdentityValue(payload.preferred_username) ||
      normalizeIdentityValue(payload.username) ||
      normalizeIdentityValue(payload.login) ||
      normalizeIdentityValue(payload.email) ||
      normalizeIdentityValue(payload.user_name) ||
      normalizeIdentityValue(payload.userId) ||
      normalizeIdentityValue(payload.uid) ||
      normalizeIdentityValue(payload.sub)
    );
  } catch {
    return '';
  }
};

const extractSearchCodeValue = (value: unknown): string => {
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  if (!value || typeof value !== 'object') {
    return '';
  }

  const payload = value as Record<string, unknown>;
  const rawCode = payload.code;
  const rawValue = payload.value;
  const rawLabel = payload.label;

  if (typeof rawCode === 'string' || typeof rawCode === 'number') {
    return String(rawCode);
  }

  if ((typeof rawValue === 'string' || typeof rawValue === 'number') && String(rawValue) !== '-1') {
    return String(rawValue);
  }

  if (typeof rawLabel === 'string' || typeof rawLabel === 'number') {
    return String(rawLabel);
  }

  return '';
};

export const normalizeSearchCode = (value: unknown): string =>
  extractSearchCodeValue(value).replace(/\D/g, '').slice(0, 10).trim();

export const buildCounterpartyCacheStorageKey = ({
  mode,
  userName,
  token,
}: {
  mode: TCounterpartySelectorMode;
  userName: string;
  token?: string | null;
}): string => {
  const identityKey =
    getIdentityFromSource(userName) || getIdentityFromToken(token) || UNKNOWN_USER_CACHE_KEY;

  return `${COUNTERPARTY_SEARCH_CACHE_PREFIX}:${mode}:${identityKey}`;
};

export const parseCachedCounterpartySearch = (
  rawValue: string | null
): TCounterpartySearchCachePayload | null => {
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue) as Partial<TCounterpartySearchCachePayload>;
    const code = normalizeSearchCode(parsed.code);
    const lastLoginAt = String(parsed.lastLoginAt ?? '').trim();

    if (!code || !lastLoginAt || !dayjs(lastLoginAt).isValid()) {
      return null;
    }

    return { code, lastLoginAt };
  } catch {
    return null;
  }
};

export const getCachedCounterpartySearch = (
  storageKey: string
): TCounterpartySearchCachePayload | null => {
  const localStorageInstance = getLocalStorageInstance();

  if (!localStorageInstance) {
    return null;
  }

  return parseCachedCounterpartySearch(localStorageInstance.getItem(storageKey));
};

export const saveCounterpartySearchToCache = (
  storageKey: string,
  code: string
): TCounterpartySearchCachePayload | null => {
  const localStorageInstance = getLocalStorageInstance();
  const normalizedCode = normalizeSearchCode(code);

  if (!localStorageInstance || !normalizedCode) {
    return null;
  }

  const payload: TCounterpartySearchCachePayload = {
    code: normalizedCode,
    lastLoginAt: new Date().toISOString(),
  };

  try {
    localStorageInstance.setItem(storageKey, JSON.stringify(payload));
    return payload;
  } catch {
    return null;
  }
};

export const isCounterpartyCodeLengthValid = (value: string): boolean =>
  counterpartyCodeValidLengths.includes(value.length as 8 | 10);

export const buildCounterpartyDetailsPath = (contragentId?: number): string =>
  `/covenants/contragents/${contragentId}/my`;

export const buildCounterpartyBlackListPath = (contragentId?: number): string =>
  `/covenants/contragents/${contragentId}/black-list`;

export const formatCounterpartyCacheDate = (
  value?: string,
  format = 'DD.MM.YYYY HH:mm'
): string => {
  if (!value || !dayjs(value).isValid()) {
    return '';
  }

  return dayjs(value).format(format);
};

export const buildCachedSearchOptions = ({
  cachedSearch,
  dateFormat,
  datePrefix,
}: {
  cachedSearch: TCounterpartySearchCachePayload | null;
  dateFormat: string;
  datePrefix: string;
}): TCounterpartyAutocompleteOption[] => {
  if (!cachedSearch) {
    return [];
  }

  const lastLoginAtLabel = formatCounterpartyCacheDate(cachedSearch.lastLoginAt, dateFormat);
  const optionLabel = lastLoginAtLabel
    ? `${cachedSearch.code} (${datePrefix}: ${lastLoginAtLabel})`
    : cachedSearch.code;

  return [
    {
      label: optionLabel,
      value: cachedSearch.code,
      lastLoginAt: cachedSearch.lastLoginAt,
    },
  ];
};
